import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import axios from "axios";
import { 
  Shield, Users, Activity, Scissors, Calendar, 
  CheckCircle, XCircle, Trash2, LogOut, RefreshCw,
  AlertCircle, DollarSign, KeyRound
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_BADGES = {
  pending_verification: { label: "Falta Verificar Correo", color: "bg-zinc-500/10 text-zinc-400 border-zinc-500/20" },
  pending: { label: "Pendiente de Pago", color: "bg-amber-500/10 text-amber-500 border-amber-500/20" },
  approved: { label: "Activo (Pagado)", color: "bg-green-500/10 text-green-500 border-green-500/20" },
  rejected: { label: "Rechazado", color: "bg-red-500/10 text-red-500 border-red-500/20" }
};

const SuperAdminDashboard = () => {
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();
  
  const [stats, setStats] = useState({ total_admins: 0, pending_admins: 0, approved_admins: 0, total_appointments: 0, total_cuts: 0, monthly_revenue: 0 });
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Estado para la nueva contraseña
  const [newPassword, setNewPassword] = useState("");
  const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [statsRes, adminsRes] = await Promise.all([
        axios.get(`${API}/superadmin/stats`, { headers }),
        axios.get(`${API}/superadmin/admins`, { headers })
      ]);
      setStats(statsRes.data);
      setAdmins(adminsRes.data);
    } catch (error) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        toast.error("Sesión expirada o acceso denegado");
        logout();
        navigate("/admin/login");
      } else {
        toast.error("Error al cargar datos del sistema");
      }
    } finally {
      setLoading(false);
    }
  }, [token, logout, navigate]);

  useEffect(() => {
    if (user?.role !== "superadmin") {
      navigate("/admin/login");
      return;
    }
    fetchData();
  }, [user, fetchData, navigate]);

  // --- ACCIONES DE SUPER ADMIN ---
  const handleApprove = async (id, shopName) => {
    try {
      await axios.put(`${API}/superadmin/admins/${id}/approve`, {}, { headers });
      toast.success(`Barbería "${shopName}" aprobada y activada.`);
      fetchData();
    } catch (error) {
      toast.error("Error al aprobar la cuenta");
    }
  };

  const handleReject = async (id, shopName) => {
    if (!window.confirm(`¿Estás seguro de rechazar a "${shopName}"? No podrán iniciar sesión.`)) return;
    try {
      await axios.put(`${API}/superadmin/admins/${id}/reject`, {}, { headers });
      toast.success(`Cuenta rechazada correctamente.`);
      fetchData();
    } catch (error) {
      toast.error("Error al rechazar la cuenta");
    }
  };

  const handleDelete = async (id, shopName) => {
    if (!window.confirm(`⚠️ ¡PELIGRO! ¿Borrar a "${shopName}" por completo? Esto eliminará todas sus citas, cortes e inventario. NO SE PUEDE DESHACER.`)) return;
    try {
      await axios.delete(`${API}/superadmin/admins/${id}`, { headers });
      toast.success(`Barbería eliminada de la base de datos.`);
      fetchData();
    } catch (error) {
      toast.error("Error al eliminar la cuenta");
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/admin/login");
  };

  // --- NUEVA ACCIÓN: CAMBIAR CONTRASEÑA ---
  const handleUpdatePassword = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) {
      toast.error("La contraseña debe tener al menos 6 caracteres.");
      return;
    }

    setIsUpdatingPassword(true);
    try {
      const response = await axios.put(`${API}/superadmin/update-password`, {
        email: user.email,
        new_password: newPassword
      }, { headers });

      if (response.data.success) {
        toast.success("✅ ¡Contraseña maestra actualizada con éxito!");
        setNewPassword("");
      } else {
        toast.error(response.data.error || "Error al actualizar la contraseña");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error de conexión al servidor");
    } finally {
      setIsUpdatingPassword(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#09090b]">
      {/* 👑 NAVBAR CON ÁREA SEGURA 👑 */}
      <nav className="sticky top-0 z-50 backdrop-blur-xl bg-black/60 border-b border-amber-500/20 pt-[max(1rem,env(safe-area-inset-top))]">
        <div className="max-w-7xl mx-auto px-6 pb-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-500 rounded-lg flex items-center justify-center shadow-[0_0_15px_rgba(245,158,11,0.4)]">
              <Shield className="w-6 h-6 text-black" />
            </div>
            <div>
              <span className="font-heading text-xl font-black uppercase tracking-widest text-white block leading-none">
                BOOSE ENTERPRISE<span className="text-amber-500">.</span>
              </span>
              <span className="text-[10px] text-amber-500 font-bold uppercase tracking-widest">Cuartel General</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={fetchData} variant="ghost" size="icon" className="text-zinc-400 hover:text-white">
              <RefreshCw className={`w-5 h-5 ${loading ? "animate-spin" : ""}`} />
            </Button>
            <Button onClick={handleLogout} variant="ghost" size="icon" className="text-zinc-400 hover:text-red-500">
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 py-8">
        
        {/* 📊 MÉTRICAS GLOBALES */}
        <div className="mb-8">
          <h2 className="text-white font-heading font-bold text-2xl uppercase mb-4 flex items-center gap-2">
            <Activity className="w-6 h-6 text-amber-500" /> Rendimiento Global
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
            
            {/* 💰 LA CAJA REGISTRADORA DE BOOSE ENTERPRISE */}
            <div className="bg-zinc-900 border border-amber-500/50 p-5 rounded-xl shadow-[0_0_15px_rgba(245,158,11,0.2)] md:col-span-2">
              <span className="text-amber-500 text-xs font-bold uppercase flex items-center gap-1 mb-1">
                <DollarSign className="w-4 h-4" /> Ingresos Mensuales (MRR)
              </span>
              <div className="flex items-baseline gap-1">
                <span className="text-zinc-500 font-bold text-xl">$</span>
                <p className="font-heading text-4xl font-black text-amber-500">{stats.monthly_revenue || 0}</p>
                <span className="text-zinc-500 font-bold text-sm">USD</span>
              </div>
            </div>

            <div className="bg-zinc-900 border border-zinc-800 p-5 rounded-xl">
              <span className="text-zinc-500 text-xs font-bold uppercase flex items-center gap-1 mb-1"><Users className="w-3 h-3" /> Clientes</span>
              <p className="font-heading text-3xl font-black text-white">{stats.total_admins}</p>
            </div>
            <div className="bg-zinc-900 border border-green-500/20 p-5 rounded-xl">
              <span className="text-green-500 text-xs font-bold uppercase flex items-center gap-1 mb-1"><CheckCircle className="w-3 h-3" /> Activos</span>
              <p className="font-heading text-3xl font-black text-green-500">{stats.approved_admins}</p>
            </div>
            <div className="bg-zinc-900 border border-zinc-800 p-5 rounded-xl">
              <span className="text-zinc-500 text-xs font-bold uppercase flex items-center gap-1 mb-1"><Calendar className="w-3 h-3" /> Citas</span>
              <p className="font-heading text-3xl font-black text-white">{stats.total_appointments}</p>
            </div>
            <div className="bg-zinc-900 border border-zinc-800 p-5 rounded-xl">
              <span className="text-zinc-500 text-xs font-bold uppercase flex items-center gap-1 mb-1"><Scissors className="w-3 h-3" /> Cortes</span>
              <p className="font-heading text-3xl font-black text-white">{stats.total_cuts}</p>
            </div>
          </div>
        </div>

        {/* 🏢 LISTA DE BARBERÍAS */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden shadow-2xl mb-8">
          <div className="p-6 border-b border-zinc-800 flex items-center justify-between">
            <h2 className="text-white font-heading font-bold text-xl uppercase">Gestión de Barberías</h2>
            <span className="text-xs font-bold bg-amber-500/10 text-amber-500 px-3 py-1 rounded-full border border-amber-500/20">
              {admins.length} Registradas
            </span>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead className="bg-zinc-950 text-zinc-400 font-heading uppercase text-xs">
                <tr>
                  <th className="px-6 py-4">Barbería</th>
                  <th className="px-6 py-4">Propietario</th>
                  <th className="px-6 py-4">Estado</th>
                  <th className="px-6 py-4">Fecha Registro</th>
                  <th className="px-6 py-4 text-right">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-800">
                {admins.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="px-6 py-12 text-center text-zinc-500">
                      <AlertCircle className="w-12 h-12 mx-auto mb-3 opacity-20" />
                      Aún no hay clientes registrados en la plataforma.
                    </td>
                  </tr>
                ) : (
                  admins.map((admin) => (
                    <tr key={admin.id} className="hover:bg-zinc-800/50 transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <img 
                            src={admin.logo_url || "/logo192.png"} 
                            alt="Logo" 
                            className="w-10 h-10 rounded-md bg-zinc-800 object-cover"
                            onError={(e) => { e.target.src = "/logo192.png"; }}
                          />
                          <div>
                            <p className="text-white font-bold">{admin.shop_name}</p>
                            <p className="text-xs text-zinc-500">/{admin.slug}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-zinc-300 font-medium">{admin.name}</p>
                        <p className="text-xs text-zinc-500">{admin.email}</p>
                        <p className="text-xs text-zinc-500">{admin.phone}</p>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-3 py-1 text-xs font-bold uppercase rounded-full border ${STATUS_BADGES[admin.status]?.color || "bg-zinc-800 text-zinc-400"}`}>
                          {STATUS_BADGES[admin.status]?.label || admin.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-zinc-400 whitespace-nowrap">
                        {new Date(admin.created_at).toLocaleDateString('es-DO')}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          {admin.status !== "approved" && (
                            <Button 
                              onClick={() => handleApprove(admin.id, admin.shop_name)} 
                              size="sm" 
                              className="bg-green-500/10 text-green-500 hover:bg-green-500 hover:text-black font-bold text-xs"
                              title="Aprobar manualmente (Si te pagaron en efectivo/transferencia)"
                            >
                              <CheckCircle className="w-4 h-4 mr-1" /> Aprobar
                            </Button>
                          )}
                          {admin.status === "approved" && (
                            <Button 
                              onClick={() => handleReject(admin.id, admin.shop_name)} 
                              size="sm" variant="outline" 
                              className="border-zinc-700 text-amber-500 hover:bg-amber-500/10 font-bold text-xs"
                              title="Suspender cuenta"
                            >
                              <XCircle className="w-4 h-4 mr-1" /> Suspender
                            </Button>
                          )}
                          <Button 
                            onClick={() => handleDelete(admin.id, admin.shop_name)} 
                            size="icon" variant="ghost" 
                            className="text-zinc-500 hover:text-red-500 hover:bg-red-500/10 h-8 w-8"
                            title="Eliminar por completo"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* 🛡️ SEGURIDAD DEL SISTEMA (NUEVO) */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden shadow-xl max-w-2xl">
          <div className="p-6 border-b border-zinc-800">
            <h2 className="text-white font-heading font-bold text-xl uppercase flex items-center gap-2">
              <KeyRound className="w-5 h-5 text-zinc-400" /> Seguridad de la Cuenta
            </h2>
            <p className="text-zinc-500 text-sm mt-1">
              Cambia la contraseña maestra de acceso al Cuartel General.
            </p>
          </div>
          <div className="p-6">
            <form onSubmit={handleUpdatePassword} className="flex flex-col sm:flex-row gap-4">
              <input 
                type="password" 
                placeholder="Escribe tu nueva contraseña segura..." 
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="flex-1 p-3 rounded-lg bg-black text-white border border-zinc-700 focus:border-amber-500 outline-none transition-colors"
                required
              />
              <Button 
                type="submit" 
                disabled={isUpdatingPassword}
                className="bg-zinc-800 text-white hover:bg-zinc-700 hover:text-white border border-zinc-700"
              >
                {isUpdatingPassword ? "Actualizando..." : "Actualizar Contraseña"}
              </Button>
            </form>
          </div>
        </div>

      </div>
    </div>
  );
};

export default SuperAdminDashboard;